import { useState, useEffect } from 'react';
import { DateUtils, Utils } from 'mw-style-react';

const _SNIPPETS_LS_KEY_ = 'snippetsCache';
const SNIPPET_CACHE_TTL = 60 * 60 * 24;
const LIMIT = 15;

const useCachedSnippets = () => {
  const [snippetsCache, setSnippetsCache] = useState({});

  /**
   * Get cached snippets from LocalStorage
   */
  const getStorageSnippets = () => {
    let cachedSnippets;
    try {
      cachedSnippets = JSON.parse(Utils.fromStorage(_SNIPPETS_LS_KEY_));
    } catch (e) {
      cachedSnippets = {};
    }
    return cachedSnippets || {};
  };

  const prepareAnswer = (record) => {
    return {
      list: record?.data ?? [],
      endList: record?.endList ?? false,
      limit: LIMIT,
      offset: record?.data?.length ?? 0,
    };
  };

  /**
   * Get cached search results
   */
  const handleSearchCached = (query) => {
    // Check for exact match first
    if (snippetsCache[query]) {
      return prepareAnswer(snippetsCache[query]);
    }

    // Look for partial matches
    const partialMatchKey = Object.keys(snippetsCache).find((key) =>
      key.includes(query)
    );

    return prepareAnswer(
      partialMatchKey ? snippetsCache[partialMatchKey] : null
    );
  };

  /**
   * Add new search result to cache
   */
  const handleUpdateCache = (query, data, assign = false) => {
    // Destructure directly from snippetsCache[query] with default to empty object
    const { data: currentList = [] } = snippetsCache[query] || {};

    // Map only the needed properties from data
    const list = data.map(({ id, title, description, data }) => ({
      id,
      title,
      description,
      data,
    }));

    // Calculate expiration time
    const expire = DateUtils.unixtime() + SNIPPET_CACHE_TTL;

    // Create updated cache entry
    const cacheEntry = {
      data: assign ? [...currentList, ...list] : list,
      expire,
      endList: data.length === 0,
    };

    // Update cache object - create a new reference to trigger state updates
    const newCache = { ...snippetsCache, [query]: cacheEntry };

    // Update state and persist to storage
    setSnippetsCache(newCache);
    Utils.toStorage(_SNIPPETS_LS_KEY_, JSON.stringify(newCache));
    return cacheEntry;
  };

  /**
   * Clear expired snippet lists from LocalStorage
   */
  const removeExpiredCache = () => {
    const actualCache = getStorageSnippets();
    const now = DateUtils.unixtime();
    for (const key of Object.keys(actualCache)) {
      if (actualCache[key].expire < now) delete actualCache[key];
    }
    Utils.toStorage(_SNIPPETS_LS_KEY_, JSON.stringify(actualCache));
  };

  useEffect(() => {
    const snippets = getStorageSnippets();
    setSnippetsCache(snippets);
  }, []);

  return { handleSearchCached, handleUpdateCache, removeExpiredCache };
};

export default useCachedSnippets;
